import { FC, useEffect, useMemo, useRef, useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import { Box, TextField, Typography } from "@mui/material";
import debounce from "lodash/debounce";
import { useConfirm } from "material-ui-confirm";

import { AnnotationType } from "../../../API";
import FullPageLoader from "../../../common/components/item/FullPageLoader";
import ItemDetailHeader from "../../../common/components/item/ItemDetailHeader";
import MainPaperWrapper from "../../../common/components/item/MainPaperWrapper";
import ResponsiveSplitContainer from "../../../common/components/item/ResponsiveSplitContainer";
import ControlTable from "../../../common/components/stream/ControlTable";
import FocusControls from "../../../common/components/stream/FocusControls";
import FocusRectOverlay from "../../../common/components/stream/FocusRectOverlay";
import BreadcrumbNavigation from "../../../common/components/tabs/BreadcrumbNavigation";
import { parseAnnotationPolygon } from "../../../common/helpers/annotationPolygon";
import confirmDialogStyleOptions from "../../../common/helpers/confirmDialogParams";
import createS3KeyForZone from "../../../common/helpers/createS3KeyForZone";
import {
  DEFAULT_FOCUS_RECT,
  defaultManualFocusDistance,
  isDefaultRect,
  parseStoredFocusDistance,
  polygonToRect,
  rectToPolygon,
} from "../../../common/helpers/focusUtils";
import mqttPublish from "../../../common/helpers/mqttPublish";
import { validateZoneName } from "../../../common/helpers/validateZoneName";
import { zoneIdsMatch } from "../../../common/helpers/zoneIdMatch";
import useBeforeUnload from "../../../common/hooks/useBeforeUnload";
import { useCustomerIdGuard } from "../../../common/hooks/useCustomerIdGuard";
import {
  PAN_DEFAULT,
  TILT_DEFAULT,
  ZOOM_DEFAULT,
  usePTController,
} from "../../../common/hooks/usePTController";
import { useStreamProcesses } from "../../../common/hooks/useStreamProcesses";
import StyledLoadingButton from "../../../common/providers/theme/design-tokens/LoadingButton/StyledLoadingButton";
import { extractDeviceNameFromId } from "../../../common/utils/getEntityName";
import { DEFAULT_MEDIA_HEIGHT } from "../../../common/variables/common";
import { errorNotification } from "../../../common/variables/notification";
import HLSStreamPlayer from "../../data-hub/components/liveview/components/cameraView/HLSStreamPlayer";
import { useSyncShadow } from "../../model-manager/hooks/useSyncShadow";
import { useCreateAnnotation } from "../hooks/useCreateAnnotation";
import { IDeviceTableRow, IModel } from "../hooks/useDeviceTableRows";
import { useGetAnnotations } from "../hooks/useGetAnnotations";
import useGetDeviceById from "../hooks/useGetDeviceByUrl";
import { useGetZone } from "../hooks/useGetZone";
import { usePublishNode } from "../hooks/usePublishNode";
import { useUpdateAnnotation } from "../hooks/useUpdateAnnotation";
import { useListenToDeleteZone } from "../zones-setup-page/hooks/useListenToDeleteZone";
import { useUpdateZone } from "../zones-setup-page/hooks/useUpdateZone";

const EditZonePage: FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const confirm = useConfirm();

  const customerId = useCustomerIdGuard();

  const { updateZone, loading } = useUpdateZone(true);

  const { publishNode } = usePublishNode();
  const { requestDeviceImage, setFocus } = mqttPublish();
  const { createAnnotation } = useCreateAnnotation();
  const { updateAnnotation } = useUpdateAnnotation();

  const { syncShadowsForNode } = useSyncShadow();

  // TODO: deviceId from params is DE# without C#
  const param = useParams<{ deviceId: string; zoneId: string }>();
  const { deviceId = "", zoneId = "" } = param;
  const {
    data,
    getZone,
    loading: getZoneLoading,
  } = useGetZone(deviceId, zoneId);

  const [searchParams] = useSearchParams();

  const deviceNameFromUrl =
    location.state?.deviceName ?? searchParams.get("deviceName");

  const [fetchingDeviceData, setFetchingDeviceData] = useState(false);

  const nodeId = location.state?.nodeId ?? searchParams.get("nodeId");

  const [ptStarted, setPtStarted] = useState(false);

  const [deviceDetails, setDeviceDetails] = useState<IDeviceTableRow | null>(
    null
  );

  const [status, setStatus] = useState(false);

  const [shouldNotify, setShouldNotify] = useState(true);

  const [zoneName, setZoneName] = useState("");
  const [focusMode, setFocusMode] = useState("auto");
  const [focusDistance, setFocusDistance] = useState(
    String(defaultManualFocusDistance())
  );
  const [focusRect, setFocusRect] = useState<{
    x1: number;
    y1: number;
    x2: number;
    y2: number;
  } | null>(null);
  const [existingAutofocusAnnotationId, setExistingAutofocusAnnotationId] =
    useState<string | null>(null);

  useEffect(() => {
    setFocusRect(null);

    setExistingAutofocusAnnotationId(null);
  }, [zoneId]);

  useEffect(() => {
    setStatus(location.state?.status ?? false);
  }, [location.state?.status]);

  useEffect(() => {
    setShouldNotify(location.state?.shouldNotify ?? true);
  }, [location.state?.shouldNotify]);

  useEffect(() => {
    setZoneName(location.state?.name ?? data?.getZone?.name ?? "");
  }, [location.state?.name, data?.getZone?.name]);

  const { fetchDevice, isServiceLoaded } = useGetDeviceById();

  const { startStreaming, stopStreaming, isVideoLoaded } = useStreamProcesses();

  const { data: deletedZoneData } = useListenToDeleteZone(zoneId);

  const serviceId =
    data?.getZone?.serviceId ?? deviceDetails?.models[0]?.serviceId;

  const { data: annotationsData } = useGetAnnotations({
    zoneId,
    serviceId: serviceId ?? "",
    deviceId: customerId ? customerId + "#" + deviceId : "",
  });

  // Load existing focus settings from zone data (ignore stale Apollo data from other zones)
  useEffect(() => {
    if (!data?.getZone || !zoneIdsMatch(data.getZone.id, zoneId)) {
      return;
    }

    const zone = data.getZone;

    setFocusMode(zone.focusMode ?? "auto");

    setFocusDistance(
      zone.focusDistance ?? String(defaultManualFocusDistance())
    );
  }, [
    data?.getZone?.id,
    data?.getZone?.focusMode,
    data?.getZone?.focusDistance,
    zoneId,
  ]);

  // Load existing AUTOFOCUS_RECT for this zone only (route param is source of truth — getZone data can lag after navigation)
  useEffect(() => {
    const items = annotationsData?.getAnnotations?.items;

    if (!items) {
      return;
    }

    const autofocusAnnotation = items.find(
      a =>
        a?.annotationType === AnnotationType.AUTOFOCUS_RECT &&
        zoneIdsMatch(a?.zoneId, zoneId)
    );

    if (autofocusAnnotation) {
      setExistingAutofocusAnnotationId(autofocusAnnotation.id);

      const parsed = parseAnnotationPolygon(autofocusAnnotation.polygon);

      if (parsed?.annotations?.length) {
        setFocusRect(polygonToRect(parsed.annotations));
      }
    } else {
      setExistingAutofocusAnnotationId(null);

      setFocusRect(null);
    }
  }, [annotationsData?.getAnnotations?.items, zoneId]);

  const debouncedSendFocus = useMemo(
    () =>
      debounce(
        (
          mode: string,
          rect: { x1: number; y1: number; x2: number; y2: number } | null,
          distance: string
        ) => {
          const { extractedDeviceName } = extractDeviceNameFromId(deviceId);

          setFocus({
            deviceId: extractedDeviceName,
            nodeId,
            focusMode: mode as "auto" | "manual",
            focusRect: mode === "auto" ? rect ?? DEFAULT_FOCUS_RECT : undefined,
            focusDistance:
              mode === "manual"
                ? parseStoredFocusDistance(distance)
                : undefined,
          });
        },
        500
      ),
    [nodeId, deviceId]
  );

  useEffect(() => {
    debouncedSendFocus.cancel();

    return () => {
      debouncedSendFocus.cancel();
    };
  }, [zoneId, debouncedSendFocus]);

  // store deviceDetail in ref to stop streaming and PT control when leaving the page
  const deviceDetailsRef = useRef(deviceDetails);

  const {
    pan,
    tilt,
    zoom,
    disabled,
    movePtUp,
    movePtDown,
    movePtLeft,
    movePtRight,
    handleGoToPt,
    handlePanInputChange,
    handleTiltInputChange,
    handleZoomInputChange,
    validationState,
    resetValidationState,
    validateCreateZoneForm,
  } = usePTController({
    deviceId,
    deviceName: deviceNameFromUrl,
    nodeId,
    deviceData: deviceDetails?.deviceData,
    currentPosition: deviceDetails?.currentPosition,
    zoneName: data?.getZone?.name ?? zoneName,
  });

  const stopStreamingAndPTControl = () => {
    if (deviceDetailsRef.current) {
      const { extractedDeviceName } = extractDeviceNameFromId(
        deviceDetailsRef.current.rowId
      );

      const runningService =
        deviceDetails?.deviceData?.hasPanTilt &&
        deviceDetails?.models?.find((model: IModel) => model.isRunning);

      const serviceType: string | null = runningService
        ? runningService.serviceName
        : null;

      stopStreaming({
        deviceId: deviceDetailsRef.current.rowId,
        nodeId: deviceDetailsRef.current.node.id,
        hasPanTilt: deviceDetailsRef.current.deviceData?.hasPanTilt,
      });

      publishNode({
        message: JSON.stringify({
          TARGET: "PANTILT",
          ACTION: "STOP_PT_CONTROL",
          device_name: extractedDeviceName,
        }),
        nodeId,
      }).then(() => {
        if (runningService) {
          publishNode({
            message: JSON.stringify({
              TARGET: "MODEL",
              ACTION: "START",
              model_name: serviceType,
              instance_name: extractedDeviceName,
              DATA: {
                serviceType,
              },
            }),
            nodeId,
          });
        }

        setPtStarted(false);

        deviceDetailsRef.current = null;
      });
    }

    resetValidationState();
  };

  useEffect(() => {
    return () => {
      // stop streaming and PT control when leaving the page
      stopStreamingAndPTControl();
    };
  }, []);

  useEffect(() => {
    if (!deviceDetails) return;

    // start PT control if device has PT and it hasn't started yet
    if (deviceDetails.deviceData?.hasPanTilt && !ptStarted) {
      const { extractedDeviceName } = extractDeviceNameFromId(
        deviceDetails.rowId
      );

      const runningService =
        deviceDetails.deviceData?.hasPanTilt &&
        deviceDetails.models?.find((model: IModel) => model.isRunning);

      const serviceType: string | null = runningService
        ? runningService.serviceName
        : null;

      publishNode({
        message: JSON.stringify({
          TARGET: "PANTILT",
          ACTION: "START_PT_CONTROL",
          device_name: extractedDeviceName,
        }),
        nodeId: nodeId,
      }).then(() => {
        if (runningService) {
          publishNode({
            message: JSON.stringify({
              TARGET: "MODEL",
              ACTION: "STOP",
              model_name: serviceType,
              instance_name: extractedDeviceName,
              DATA: {
                serviceType,
              },
            }),
            nodeId,
          });
        }

        setPtStarted(true);
      });
    }
  }, [deviceDetails, ptStarted]);

  useEffect((): void => {
    if (!isServiceLoaded) return;

    setFetchingDeviceData(true);

    const getDevice = async (): Promise<IDeviceTableRow | null> => {
      try {
        const device = await fetchDevice();

        if (!device) {
          return null;
        }

        deviceDetailsRef.current = device;

        setDeviceDetails(device);

        return device;
      } catch (error) {
        errorNotification("Failed to fetch device details");

        console.error(error);

        return null;
      } finally {
        setFetchingDeviceData(false);
      }
    };

    getDevice().then((device): void => {
      if (!isVideoLoaded && device) {
        startStreaming({
          deviceId: device.rowId,
          nodeId: device.node.id,
          hasPanTilt: device.deviceData?.hasPanTilt,
        });
      }
    });
  }, [deviceId, customerId, isServiceLoaded, isVideoLoaded]);

  useBeforeUnload();

  const saveImage = (): void => {
    const { extractedDeviceName } = extractDeviceNameFromId(deviceId);

    requestDeviceImage({
      deviceId: extractedDeviceName,
      nodeId,
      s3Key: createS3KeyForZone(nodeId, zoneName, extractedDeviceName), // generate s3_key e.g C_cleanconnect/C_cleanconnect_L_tp_N_solartrailer-agx4/keyframes/tp-ch4_keyframe.jpg // also need to save it to zone item
      errorMessage: "Something went wrong when saving image to S3",
    });
  };

  // temporary fix until we fix DDB schemas with new C# prefix for querying zones, annotations, etc. (this would involve adding the prefix for device and service creation)
  // note: there is a concern that when we update a zone to share the same name as an existing zone.
  // This will lead to a conflict of keyframe names in S3.
  // However there is preliminary steps to make sure zone names are unique, so there is less urgency to fix it from a S3 key perspective.
  const updateZoneHandler = (): void => {
    const isValid = validateCreateZoneForm(zoneName, pan, tilt, zoom);

    if (!isValid) return;

    const input = {
      deviceId: customerId + "#" + deviceId,
      zoneId: zoneId,
      name: zoneName,
      status,
      pan,
      tilt,
      zoom,
      shouldNotify,
      focusMode,
      focusDistance,
    };

    const validName = validateZoneName(zoneName);

    if (!validName) {
      return;
    }

    updateZone(input)
      .then(async (): Promise<void> => {
        saveImage();

        // Handle AUTOFOCUS_RECT annotation
        if (focusMode === "auto" && focusRect && !isDefaultRect(focusRect)) {
          const annotationInput = {
            name: "Focus Area",
            polygon: JSON.stringify({
              annotations: rectToPolygon(focusRect),
            }),
            zoneId,
            serviceId: serviceId ?? "",
            deviceId: customerId + "#" + deviceId,
            nodeId,
            annotationType: AnnotationType.AUTOFOCUS_RECT,
          };

          try {
            if (existingAutofocusAnnotationId) {
              await updateAnnotation({
                ...annotationInput,
                id: existingAutofocusAnnotationId,
              });
            } else {
              await createAnnotation(annotationInput);
            }
          } catch (error) {
            console.error("Failed to save autofocus annotation:", error);
          }
        }

        stopStreamingAndPTControl();

        navigate(
          `/device/${encodeURIComponent(deviceId)}/${encodeURIComponent(
            zoneId
          )}?deviceName=${deviceNameFromUrl}&nodeId=${encodeURIComponent(
            nodeId
          )}`
        );
      })
      .finally((): void => {
        syncShadowsForNode(nodeId);
      });
  };

  const zoneLink = `/device/${encodeURIComponent(
    deviceId
  )}/${encodeURIComponent(
    zoneId
  )}?deviceName=${deviceNameFromUrl}&nodeId=${encodeURIComponent(nodeId)}`;

  const confirmCancel = (): void => {
    confirm({
      title: "Are you sure you want to cancel Zone editing?",
      confirmationText: "Cancel",
      cancellationText: "Back",
      ...confirmDialogStyleOptions,
      confirmationButtonProps: {
        ...confirmDialogStyleOptions.confirmationButtonProps,
        disabled: loading,
        color: "primary",
      },
    }).then(async (): Promise<void> => {
      stopStreamingAndPTControl();

      navigate(zoneLink);
    });
  };

  // getZone is listed for exhaustive-deps; Apollo Client 3 useLazyQuery keeps a stable execute fn.
  useEffect((): void => {
    if (!customerId || !zoneId || !deviceId) {
      return;
    }

    getZone();
  }, [customerId, zoneId, deviceId, getZone]);

  useEffect(() => {
    if (deletedZoneData?.listenToDeleteZone?.id === zoneId) {
      stopStreamingAndPTControl();

      navigate(`/device/${encodeURIComponent(deviceId)}`);
    }
  }, [deletedZoneData]);

  useEffect((): void => {
    if (data?.getZone && ptStarted) {
      const fetchedZoned = data.getZone;

      const panValue = fetchedZoned.pan ?? PAN_DEFAULT;
      const tiltValue = fetchedZoned.tilt ?? TILT_DEFAULT;
      const zoomValue = fetchedZoned.zoom ?? ZOOM_DEFAULT;
      const zoneNameValue = fetchedZoned.name;

      setStatus(fetchedZoned.status);

      setShouldNotify(fetchedZoned.shouldNotify ?? true);

      setZoneName(zoneNameValue);

      handleGoToPt(panValue, tiltValue, zoomValue, zoneNameValue);
    }
  }, [data?.getZone, ptStarted]);

  const mainZoneName = data?.getZone?.name ?? "";

  const breadcrumbItems = [
    { label: "Devices", path: "/devices" },
    {
      label: deviceNameFromUrl,
      path: "/device/" + encodeURIComponent(deviceId),
    },
    { label: mainZoneName, path: zoneLink },
    { label: `Edit ${zoneName}` },
  ];

  if (!data && (getZoneLoading || fetchingDeviceData)) {
    return <FullPageLoader />;
  }

  if (!data?.getZone) {
    return null;
  }

  return (
    <>
      <BreadcrumbNavigation items={breadcrumbItems} />

      <ItemDetailHeader name={"Edit " + mainZoneName} />

      {data?.getZone && (
        <>
          <MainPaperWrapper>
            <ResponsiveSplitContainer gap="1.5em">
              <Box>
                <Box
                  sx={{
                    width: "100%",
                  }}
                >
                  <TextField
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                      },
                    }}
                    label="Zone name"
                    value={zoneName}
                    error={validationState.zoneName.hasError}
                    helperText={validationState.zoneName.errorMessage}
                    onChange={(e): void => setZoneName(e.target.value)}
                  />
                  <Typography
                    sx={{ margin: "32px 0 8px 0" }}
                    variant="subtitle1"
                  >
                    Set up the camera position
                  </Typography>

                  <Box>
                    <ControlTable
                      pan={pan}
                      tilt={tilt}
                      zoom={zoom}
                      ptDisabled={disabled}
                      validation={validationState}
                      showValidationAlert
                      handlePanInputChange={handlePanInputChange}
                      handleGoToPt={handleGoToPt}
                      handleTiltInputChange={handleTiltInputChange}
                      handleZoomInputChange={handleZoomInputChange}
                      movePtLeft={movePtLeft}
                      movePtRight={movePtRight}
                      movePtUp={movePtUp}
                      movePtDown={movePtDown}
                      hasZoom={deviceDetails?.deviceData?.hasZoom}
                    />
                  </Box>

                  <FocusControls
                    focusMode={focusMode}
                    focusDistance={focusDistance}
                    focusRect={focusRect}
                    onFocusModeChange={mode => {
                      setFocusMode(mode);

                      debouncedSendFocus(mode, focusRect, focusDistance);
                    }}
                    onFocusDistanceChange={val => {
                      setFocusDistance(val);

                      debouncedSendFocus(focusMode, focusRect, val);
                    }}
                    onFocusRectChange={rect => {
                      setFocusRect(rect);

                      debouncedSendFocus(focusMode, rect, focusDistance);
                    }}
                    disabled={disabled}
                  />
                </Box>
              </Box>
              <Box>
                <Box sx={{ position: "relative" }}>
                  <HLSStreamPlayer fixedHeight={DEFAULT_MEDIA_HEIGHT} />
                  {focusMode === "auto" && (
                    <FocusRectOverlay
                      rect={focusRect ?? DEFAULT_FOCUS_RECT}
                      onChange={rect => {
                        setFocusRect(rect);

                        debouncedSendFocus("auto", rect, focusDistance);
                      }}
                    />
                  )}
                </Box>
              </Box>
            </ResponsiveSplitContainer>
          </MainPaperWrapper>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "1em",
            }}
          >
            <StyledLoadingButton
              sx={{
                marginTop: "1.5em",
              }}
              loading={loading}
              variant="outlined"
              color="inherit"
              onClick={confirmCancel}
            >
              Cancel
            </StyledLoadingButton>

            <StyledLoadingButton
              sx={{
                marginTop: "1.5em",
              }}
              loading={loading}
              variant="contained"
              color="primary"
              onClick={updateZoneHandler}
            >
              Save changes
            </StyledLoadingButton>
          </Box>
        </>
      )}
    </>
  );
};

export default EditZonePage;
